import {
  HttpClient,
  HttpHeaders,
  HttpDownloadProgressEvent,
  HttpEventType
} from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { environment } from '@cpd-app/environment';
import { Observable } from 'rxjs';
import { map, filter, delay, tap } from 'rxjs/operators';

import { StreamRequest, StreamResponse } from '../../models';
import { ConversationGoalsMapper } from '../../mappers/conversation-goals.mapper';
import { ResumeImportService } from '../resume-import/resume-import.service';

@Injectable({ providedIn: 'root' })
export class StreamService {
  /**
   * T.8. Resume mode, and where to say a Via answer changed the resume.
   * Optional so a caller that builds this by hand (the specs) need not know.
   */
  public constructor(
    private httpClient: HttpClient,
    @Optional() private readonly resumeImport?: ResumeImportService
  ) {}

  public connect(request: StreamRequest): Observable<StreamResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Silent-Request': 'true',
      'Cache-Control': 'no-cache'
    });

    const streamUri = environment.streamUri;
    const body: StreamRequest = this.resumeImport?.resumeMode()
      ? { ...request, resumeMode: true, resumeContext: this.resumeImport.context() || undefined }
      : { ...request, lastViaMessage: undefined };
    let resumeAnnounced = false;

    return this.httpClient
      .post(streamUri, body, {
        headers,
        observe: 'events',
        responseType: 'text',
        reportProgress: true
      })
      .pipe(
        filter(
          (event: HttpDownloadProgressEvent) =>
            event.type === HttpEventType.DownloadProgress && !!event.partialText
        ),
        map((event) => {
          const parsedData = this.parseSSEData(event.partialText);
          return parsedData;
        }),
        // T.8. The answer held resume facts: hand them to the builder, once.
        tap((parsed) => {
          const found = parsed?.data?.resumeImported;
          if (parsed?.type === 'complete' && found && !resumeAnnounced) {
            resumeAnnounced = true;
            this.resumeImport?.changed.next(found);
          }
        }),
        filter(
          (parsedData) =>
            !!parsedData?.data?.fullResponse ||
            !!parsedData?.data?.response ||
            // An error frame carries neither, which is exactly why it used to be
            // dropped before the app could show it.
            !!parsedData?.data?.error
        ),
        delay(100)
      );
  }

  private parseSSEData(text: string): StreamResponse | null {
    const lines = text.split('\n\ndata: ').filter((i) => {
      return !!i?.trim();
    });
    const lastLine = lines[lines.length - 1].replace(/data:/g, '');

    if (
      lastLine &&
      lastLine.trim().startsWith('{') &&
      lastLine.trim().endsWith('}')
    ) {
      try {
        const result = JSON.parse(lastLine);
        // An error frame anywhere in the stream is TERMINAL, so it outranks
        // whatever the backend appended after it. See findStreamError.
        const streamError = this.findStreamError(lines) ?? result.data?.error;
        const goals = result.data?.metadata?.conversationGoals;
        const messageId = +result.data?.messageId;
        const successSaveGoal = result.data?.metadata?.successSaveGoal;
        const learningSuggestions = result.data?.metadata?.learningSuggestions;

        return {
          // A failed turn is reported as a failure regardless of the trailing
          // frame, which is what makes the effect dispatch streamFailure rather
          // than falling through to streamComplete with the overload fallback.
          type: streamError ? 'error' : result.type,
          data: {
            conversationId: +result.data?.conversationId,
            goals: ConversationGoalsMapper.toConversationGoals(goals),
            courses: learningSuggestions?.courses,
            certificates: learningSuggestions?.certificates,
            successSaveGoal,
            // T.8. What a resume-mode message said that the resume can use.
            resumeImported: result.data?.metadata?.resumeImported,
            messageId,
            fullResponse: result.data?.fullResponse,
            response: result.data?.response,
            // A mid-stream failure arrives as {type:'error', data:{error}}. It was
            // never mapped here, so even reaching the filter it would have been an
            // empty frame. Without it the learner gets a half-answer that never
            // commits and a send button that stays disabled.
            error: streamError
          },
          timestamp: result.timestamp
        } as StreamResponse;
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
        return null;
      }
    }

    return null;
  }

  /**
   * The error carried by the first error frame in the stream, if there is one.
   *
   * WHY A SCAN AND NOT JUST THE LAST FRAME. `partialText` is cumulative, so this
   * parser re-reads the whole stream on every progress event and acts only on
   * the final frame. On a mid-stream failure the backend writes the error frame
   * and then a `complete` frame carrying the generic overload fallback, both in
   * one tick. The last frame is therefore the fallback, the error was silently
   * dropped, and the learner saw a blank overload message rather than what
   * actually went wrong. Reading `data.error` off the last frame alone, which is
   * what shipped, cannot reach that path at all.
   *
   * Keeping the backend's trailing `complete` matters independently: production
   * still serves an older frontend build that needs it to re-enable the send
   * button, so the fix belongs on this side of the wire.
   *
   * Matches on the FRAME, never on the text. Via answers questions about JST
   * errors routinely, so a substring search over the stream would report a
   * healthy turn as a failure.
   */
  private findStreamError(blocks: string[]): string | undefined {
    for (const block of blocks) {
      const candidate = block.replace(/data:/g, '').trim();
      if (!candidate.startsWith('{') || !candidate.endsWith('}')) continue;
      try {
        const frame = JSON.parse(candidate);
        if (frame?.type === 'error' && frame?.data?.error) return frame.data.error;
      } catch {
        // A partially-written frame is not an error frame; keep looking.
      }
    }

    return undefined;
  }
}
